import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../Guard/AuthContext';
import { apiInstance } from '../utils/api';

export const useContactStore = () => {
  const location = useHistory();
  const {
    user,
    setUser,
    currentContactStore,
    setCurrentContactStore,
    loading,
    contactStores,
    setContactStores,
  } = useContext(AuthContext);

  const userContactStores = user?.contactStores; // тут могут быть поля { commodity, requests, accounts, marketing, current, crmId }

  // --- Автовыбор: если у пользователя ровно 1 магазин и он ещё не помечен current ---
  useEffect(() => {
    if (loading) return; // ждём пока загрузится user
    if (!userContactStores || userContactStores.length !== 1) return; // интересует ровно один
    const single = userContactStores[0];
    if (single.current) return; // уже current
    // Делаем запрос чтобы зафиксировать его как текущий на бэке и в локальном состоянии
    (async () => {
      try {
        await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/user/change-contact-store?crmId=${single.crmId}`,
          'POST',
          null
        );

        // Обновляем user.contactStores с проставленным current
        const updatedUserStores = userContactStores.map((s) => ({
          ...s,
          current: s.crmId === single.crmId,
        }));
        setUser((prev) => (prev ? { ...prev, contactStores: updatedUserStores } : prev));

        // Обновляем contactStores в контексте если уже есть данные магазинов (иначе основной эффект позже объединит)
        if (contactStores && contactStores.length > 0) {
          setContactStores(contactStores.map((s) => ({ ...s, current: s.crmId === single.crmId })));
        }

        // Устанавливаем currentContactStore
        setCurrentContactStore((prev) =>
          prev && prev.crmId === single.crmId ? prev : { ...single, current: true }
        );
      } catch (e) {
        console.error(
          '[useContactStore] Автоустановка единственного магазина как текущего не удалась',
          e
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, userContactStores]);

  // Fetch contact stores from backend и объединяем поля
  useEffect(() => {
    // Ждем завершения загрузки пользователя
    if (loading) {
      return;
    }

    // Если пользователь загружен, но у него нет contactStores
    if (!userContactStores || userContactStores.length === 0) {
      return;
    }

    // Сначала найдем current магазин из данных пользователя
    const currentStoreFromUser = userContactStores.find((store) => store.current);
    const crmIds = userContactStores.map((s) => s.crmId).filter(Boolean);
    if (!crmIds.length) {
      return;
    }

    const filterQuery = crmIds
      .map((id, index) => `filters[crmId][$in][${index}]=${encodeURIComponent(id)}`)
      .join('&');

    const url = `${
      process.env.REACT_APP_API_URL
    }/api/contact-stores?${filterQuery}&populate=*`;

    apiInstance(url, 'GET', null)
      .then((response) => {
        const apiData = response.data;

        if (!Array.isArray(apiData)) {
          return;
        }

        const stores = apiData.map((entry) => {
          const id = entry.id;
          const attrs = entry.attributes || {};
          const crmId = attrs.crmId;
          const matchedStore = userContactStores.find((cs) => cs.crmId === crmId) || {};

          let type = null;
          if (attrs.type?.data) {
            type = {
              id: attrs.type.data.id,
              ...(attrs.type.data.attributes || {}),
            };
          }

          let location = null;
          if (attrs.location?.data) {
            location = {
              id: attrs.location.data.id,
              ...(attrs.location.data.attributes || {}),
            };
          }

          // ВАЖНО: name берем из API данных торговых точек, а permissions из пользователя
          const combinedStore = {
            ...matchedStore, // данные пользователя (permissions, current и т.д.)
            id,
            crmId,
            name: attrs.name, // название из API торговых точек
            type, // тип из API торговых точек
            location, // локация из API торговых точек
            excise: attrs.excise,
            cash: attrs.cash,
            online: attrs.online,
          };

          return combinedStore;
        });

        // Также обновляем contactStores в AuthContext
        setContactStores(stores);

        // Если у нас есть current магазин из пользователя, найдем соответствующий из API и объединим данные
        if (currentStoreFromUser) {
          const matchingStoreFromAPI = stores.find(
            (store) => store.crmId === currentStoreFromUser.crmId
          );

          if (matchingStoreFromAPI) {
            // Объединяем данные: название и тип из API, permissions из пользователя
            const combinedCurrentStore = {
              ...currentStoreFromUser, // Данные пользователя (permissions, current и т.д.)
              ...matchingStoreFromAPI, // Данные из API (name, type и т.д.)
              // ПРИНУДИТЕЛЬНО устанавливаем важные поля из API
              name: matchingStoreFromAPI.name, // название из API торговых точек
              type: matchingStoreFromAPI.type, // тип из API торговых точек
              excise: matchingStoreFromAPI.excise,
              cash: matchingStoreFromAPI.cash,
              online: matchingStoreFromAPI.online,
            };
            setCurrentContactStore(combinedCurrentStore);
          } else {
            // Если не нашли в API, используем данные пользователя
            setCurrentContactStore(currentStoreFromUser);
          }
        } else if (!currentContactStore || !currentContactStore.crmId) {
          // Fallback: если нет current в пользователе, берем первый из API
          const fallbackStore = stores[0];
          setCurrentContactStore(fallbackStore);
        }
      })
      .catch((error) => {
        console.error('[useContactStore] Ошибка при получении контактных магазинов:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContactStores, loading]);

  const handleChange = useCallback(
    async (optionOrCrmId) => {
      // Поддержка передачи как crmId, так и объекта опции, так и готового store объекта
      let crmId, selectedStore;

      if (typeof optionOrCrmId === 'string') {
        // Передан просто crmId
        crmId = optionOrCrmId;
        selectedStore = contactStores?.find((store) => store.crmId === crmId);
      } else if (optionOrCrmId?.value) {
        // Передана опция {value: crmId, text: name, store: {...}}
        crmId = optionOrCrmId.value;
        selectedStore =
          optionOrCrmId.store || contactStores?.find((store) => store.crmId === crmId);
      } else if (optionOrCrmId?.crmId) {
        // Передан объект магазина напрямую
        crmId = optionOrCrmId.crmId;
        selectedStore = optionOrCrmId;
      } else {
        console.error('[useContactStore] Invalid option format:', optionOrCrmId);
        return;
      }

      if (!selectedStore) {
        console.error('[useContactStore] Контактный магазин с указанным ID не найден.', crmId);
        return;
      }

      try {
        await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/user/change-contact-store?crmId=${selectedStore.crmId}`,
          'POST',
          null
        );

        // Обновляем current флаг в локальном списке магазинов
        const updatedContactStores = (contactStores || []).map((s) => ({
          ...s,
          current: s.crmId === selectedStore.crmId,
        }));
        setContactStores(updatedContactStores);

        // Обновляем current флаг в user.contactStores, чтобы при повторном эффекте не перезаписывалось
        if (user?.contactStores) {
          const updatedUserContactStores = user.contactStores.map((s) => ({
            ...s,
            current: s.crmId === selectedStore.crmId,
          }));
          setUser({ ...user, contactStores: updatedUserContactStores });
        }

        // Устанавливаем выбранный магазин (уже с полными данными) как текущий
        setCurrentContactStore({ ...selectedStore, current: true });

        // Перенаправление при необходимости
        if (
          location?.location?.pathname.includes('/requests/') ||
          location?.location?.pathname.includes('/edit-request/')
        ) {
          location.push('/');
        }
      } catch (error) {
        console.error('[useContactStore] Ошибка при смене контактного магазина:', error);
      }
    },
    [contactStores, setCurrentContactStore, location, user, setUser, setContactStores]
  );

  // Собираем опции для селекта (или Dropdown) из contactStores (уже объединенные данные)
  const currentContactStoreOptions = contactStores.map((store) => ({
    value: store.crmId,
    text: store.name, // Используем name из объединенных данных
    store: store, // Сохраняем полные данные магазина для удобства
  }));

  // Автоматически выбираем магазин только при первой инициализации
  useEffect(() => {
    // Выбираем магазин только если:
    // 1. Нет текущего выбранного магазина
    // 2. Есть загруженные магазины
    // 3. Это первая инициализация (данные только что загрузились)
    if (!currentContactStore && contactStores.length > 0) {
      const storeToSelect = contactStores.find((s) => s.current) || contactStores[0];
      setCurrentContactStore(storeToSelect);
    }
  }, [contactStores, currentContactStore, setCurrentContactStore]);

  return { currentContactStoreOptions, currentContactStore, handleChange };
};
